import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { PlusIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useAppDispatch, RootState } from '../../store';
import {
  selectItemSubtypeDefinitionError,
  selectItemSubtypeDefinitionLoading,
  selectItemSubtypeDefinitions,
  fetchItemSubtypeDefinitions,
  createItemSubtypeDefinition,
  updateItemSubtypeDefinition,
  deleteItemSubtypeDefinition,
  resetForm,
} from './itemSubtypeDefinitionSlice';
import { ItemSubtypeDefinition } from './itemSubtypeDefinition';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePermission } from '../../components/common/PermissionGuard';
import PageWrapper from '../../components/common/PageWrapper';
import SearchBar from '../../components/searchbar';
import { formatStatusText, capitalizeWords } from '../../utils/formatters';
import { enhancedApiClient, isSuccessResponse } from '../../services/enhancedApiClient';
import { useMasterDropdown } from '../../hooks/useMasterDropdown';

const ItemSubtypeDefinitions: React.FC = () => {
  const dispatch = useAppDispatch();
  const definitions = useSelector(selectItemSubtypeDefinitions);
  const loading = useSelector(selectItemSubtypeDefinitionLoading);
  const error = useSelector(selectItemSubtypeDefinitionError);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<ItemSubtypeDefinition | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filters
  const [filterProductType, setFilterProductType] = useState('');
  const [filterItemType, setFilterItemType] = useState('');
  const [filterSubtypeName, setFilterSubtypeName] = useState('');

  // Form fields - new structure for multiple subtypes and options
  const [productType, setProductType] = useState('');
  const [itemType, setItemType] = useState('');
  
  // Multiple subtypes structure: [{ subtypeName, selectionType, uiComponentType, isRequired, displayOrder, options: [{ value, label, optionOrder, description }] }]
  const [subtypes, setSubtypes] = useState<Array<{
    subtypeName: string;
    selectionType: 'single' | 'multi';
    uiComponentType: 'dropdown' | 'checkbox' | 'radio';
    isRequired: boolean;
    displayOrder: number;
    options: Array<{
      value: string;
      label: string;
      optionOrder: number;
      description: string;
    }>;
  }>>([{
    subtypeName: '',
    selectionType: 'single',
    uiComponentType: 'dropdown',
    isRequired: false,
    displayOrder: 0,
    options: [{ value: '', label: '', optionOrder: 0, description: '' }]
  }]);

  // Legacy fields for edit mode (single option)
  const [subtypeName, setSubtypeName] = useState('');
  const [optionValue, setOptionValue] = useState('');
  const [optionLabel, setOptionLabel] = useState('');
  const [selectionType, setSelectionType] = useState<'single' | 'multi'>('single');
  const [uiComponentType, setUiComponentType] = useState<'dropdown' | 'checkbox' | 'radio'>('dropdown');
  const [isRequired, setIsRequired] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [optionOrder, setOptionOrder] = useState(0);
  const [description, setDescription] = useState('');

  // Product types - dynamically fetched from sets master (product_masters table)
  const { options: productTypeOptions, loading: loadingProductTypes } = useMasterDropdown({
    masterType: 'sets',
    enabled: true,
  });
  
  // Extract product type names from the dropdown options
  const productTypes = productTypeOptions.map(option => option.label || option.value).filter(Boolean);
  
  // Item types - dynamically fetched from item masters
  const { options: itemTypeOptions, loading: loadingItemTypes } = useMasterDropdown({
    masterType: 'items',
    enabled: true,
  });
  
  // Extract item type names from the dropdown options
  const itemTypes = itemTypeOptions.map(option => option.label || option.value).filter(Boolean);

  // Ref to track if we've loaded subtypes for the current productType + itemType combination
  const loadedSubtypesRef = useRef<string>('');

  useEffect(() => {
    const filters: any = {};
    if (filterProductType) filters.productType = filterProductType;
    if (filterItemType) filters.itemType = filterItemType;
    if (filterSubtypeName) filters.subtypeName = filterSubtypeName;
    filters.isActive = true; // Only show active by default

    dispatch(fetchItemSubtypeDefinitions(filters));
  }, [dispatch, filterProductType, filterItemType, filterSubtypeName]);

  // Fetch existing subtypes when product type and item type are selected in create mode
  useEffect(() => {
    const fetchExistingSubtypes = async () => {
      // Only fetch in create mode when both product type and item type are selected
      if (formMode === 'create' && isFormOpen && productType && itemType) {
        const combinationKey = `${productType}-${itemType}`;
        
        // Only fetch if we haven't loaded subtypes for this combination yet
        if (loadedSubtypesRef.current === combinationKey) {
          return;
        }

        try {
          const response = await enhancedApiClient.get(
            `/item-subtype-definitions/available?productType=${encodeURIComponent(productType)}&itemType=${encodeURIComponent(itemType)}`
          );

          if (isSuccessResponse(response)) {
            const backendResponse = response.data as any;
            const config = backendResponse?.data || backendResponse;
            const existingSubtypes = config?.subtypes || [];

            // If there are existing subtypes, pre-populate the form
            if (Array.isArray(existingSubtypes) && existingSubtypes.length > 0) {
              // Group subtypes by subtypeName
              const groupedBySubtypeName: Record<string, any> = {};
              
              existingSubtypes.forEach((subtypeGroup: any) => {
                const subtypeName = subtypeGroup.subtypeName || subtypeGroup.name;
                if (!groupedBySubtypeName[subtypeName]) {
                  groupedBySubtypeName[subtypeName] = {
                    subtypeName: subtypeName,
                    selectionType: subtypeGroup.selectionType || 'single',
                    uiComponentType: subtypeGroup.uiComponentType || subtypeGroup.ui_component_type || 'dropdown',
                    isRequired: subtypeGroup.isRequired || false,
                    displayOrder: subtypeGroup.displayOrder || 0,
                    options: [],
                  };
                }
                
                // Add options from this subtype group
                if (subtypeGroup.options && Array.isArray(subtypeGroup.options)) {
                  subtypeGroup.options.forEach((option: any) => {
                    groupedBySubtypeName[subtypeName].options.push({
                      value: option.value || option.optionValue || '',
                      label: option.label || option.optionLabel || option.value || option.optionValue || '',
                      optionOrder: option.optionOrder || 0,
                      description: option.description || '',
                    });
                  });
                }
              });

              // Convert grouped object to array
              const subtypesArray = Object.values(groupedBySubtypeName);
              
              // Update with existing subtypes (even if empty, to mark as loaded)
              setSubtypes(subtypesArray);
              loadedSubtypesRef.current = combinationKey;
            } else {
              // No existing subtypes, keep subtypes array empty and mark as loaded
              setSubtypes([]);
              loadedSubtypesRef.current = combinationKey;
            }
          }
        } catch (error) {
          console.error('Error fetching existing subtypes:', error);
          // Don't show error to user, just log it - it's okay if there are no existing subtypes
          // Mark as loaded to prevent repeated failed requests
          loadedSubtypesRef.current = combinationKey;
        }
      } else {
        // Reset the ref when form is closed or mode changes
        loadedSubtypesRef.current = '';
      }
    };

    fetchExistingSubtypes();
  }, [productType, itemType, formMode, isFormOpen]);

  // Permissions
  const canCreate = usePermission('item_subtype_definitions', 'create') || usePermission('master', 'create');
  const canUpdate = usePermission('item_subtype_definitions', 'update') || usePermission('master', 'update');
  const canDelete = usePermission('item_subtype_definitions', 'delete') || usePermission('master', 'delete');
  const canRead = usePermission('item_subtype_definitions', 'read') || usePermission('master', 'read');

  // Group definitions by product type, item type, and subtype name
  const groupedDefinitions = definitions.reduce((acc, def) => {
    const key = `${def.productType}-${def.itemType}-${def.subtypeName}`;
    if (!acc[key]) {
      acc[key] = {
        productType: def.productType,
        itemType: def.itemType,
        subtypeName: def.subtypeName,
        selectionType: def.selectionType,
        isRequired: def.isRequired,
        displayOrder: def.displayOrder,
        options: [],
      };
    }
    acc[key].options.push({
      id: def.id || '',
      value: def.optionValue,
      label: def.optionLabel || def.optionValue,
      optionOrder: def.optionOrder,
      description: def.description,
      isActive: def.isActive,
    });
    return acc;
  }, {} as Record<string, any>);

  const handleCreateDefinition = () => {
    dispatch(resetForm());
    setSelectedDefinition(null);
    setFormMode('create');
    setProductType('');
    setItemType('');
    // Start with empty subtypes array - user can add subtypes if needed
    setSubtypes([]);
    loadedSubtypesRef.current = ''; // Reset the ref when creating new definition
    setIsFormOpen(true);
  };

  // Add a new subtype
  const addSubtype = () => {
    setSubtypes([...subtypes, {
      subtypeName: '',
      selectionType: 'single',
      uiComponentType: 'dropdown',
      isRequired: false,
      displayOrder: subtypes.length,
      options: [{ value: '', label: '', optionOrder: 0, description: '' }]
    }]);
  };

  // Remove a subtype
  const removeSubtype = (index: number) => {
    const updated = subtypes.filter((_, i) => i !== index);
    // If removing the last subtype, add an empty one to keep the form usable
    // But allow empty array - user can create without subtypes
    setSubtypes(updated.length > 0 ? updated : []);
  };

  // Update subtype field
  const updateSubtype = (index: number, field: string, value: any) => {
    const updated = [...subtypes];
    updated[index] = { ...updated[index], [field]: value };
    setSubtypes(updated);
  };

  // Add option to a subtype
  const addOption = (subtypeIndex: number) => {
    const updated = [...subtypes];
    updated[subtypeIndex].options.push({
      value: '',
      label: '',
      optionOrder: updated[subtypeIndex].options.length,
      description: ''
    });
    setSubtypes(updated);
  };

  // Remove option from a subtype
  const removeOption = (subtypeIndex: number, optionIndex: number) => {
    const updated = [...subtypes];
    updated[subtypeIndex].options = updated[subtypeIndex].options.filter((_, i) => i !== optionIndex);
    setSubtypes(updated);
  };

  // Update option field
  const updateOption = (subtypeIndex: number, optionIndex: number, field: string, value: any) => {
    const updated = [...subtypes];
    updated[subtypeIndex].options[optionIndex] = {
      ...updated[subtypeIndex].options[optionIndex],
      [field]: value
    };
    setSubtypes(updated);
  };

  const handleEditDefinition = (definition: ItemSubtypeDefinition) => {
    setProductType(definition.productType);
    setItemType(definition.itemType);
    setSubtypeName(definition.subtypeName);
    setOptionValue(definition.optionValue);
    setOptionLabel(definition.optionLabel || '');
    setSelectionType(definition.selectionType);
    setUiComponentType((definition as any).uiComponentType || 'dropdown');
    setIsRequired(definition.isRequired);
    setIsActive(definition.isActive);
    setDisplayOrder(definition.displayOrder);
    setOptionOrder(definition.optionOrder);
    setDescription(definition.description || '');
    setSelectedDefinition(definition);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleFormSubmit = async () => {
    if (!productType || !itemType) {
      alert('Please select Product Type and Item Type');
      return;
    }

    // Filter out empty subtypes (no subtype name or no valid options)
    const validSubtypes = subtypes.filter(subtype => {
      const hasSubtypeName = subtype.subtypeName && subtype.subtypeName.trim() !== '';
      const hasValidOptions = subtype.options.some(opt => opt.value && opt.value.trim() !== '');
      return hasSubtypeName && hasValidOptions;
    });

    // Validate subtypes that have names - if a subtype has a name, it must have at least one valid option
    for (let i = 0; i < subtypes.length; i++) {
      const subtype = subtypes[i];
      const hasSubtypeName = subtype.subtypeName && subtype.subtypeName.trim() !== '';
      const hasValidOptions = subtype.options.some(opt => opt.value && opt.value.trim() !== '');
      
      if (hasSubtypeName && !hasValidOptions) {
        alert(`Subtype "${subtype.subtypeName}": Please add at least one valid option`);
        return;
      }
    }

    try {
      if (formMode === 'create') {
        // Build definitions array for bulk create - only for subtypes with valid options
        const definitions: Partial<ItemSubtypeDefinition>[] = [];
        
        // Add valid subtypes with options only
        validSubtypes.forEach((subtype, subtypeIndex) => {
          subtype.options.forEach((option, optionIndex) => {
            if (option.value && option.value.trim() !== '') {
              definitions.push({
                productType,
                itemType,
                subtypeName: subtype.subtypeName.trim(),
                optionValue: option.value.trim(),
                optionLabel: option.label.trim() || option.value.trim(),
                selectionType: subtype.selectionType,
                uiComponentType: subtype.uiComponentType || 'dropdown',
                isRequired: subtype.isRequired,
                isActive: true,
                displayOrder: subtype.displayOrder,
                optionOrder: option.optionOrder,
                description: option.description.trim() || null,
              });
            }
          });
        });

        // Only create entries if there are valid subtypes with options
        if (definitions.length === 0) {
          alert('Please add at least one subtype with at least one option value. Entries without options will not be created.');
          return;
        }

        // Use bulk create API
        const response = await enhancedApiClient.post('/item-subtype-definitions/bulk', { definitions });
        if (isSuccessResponse(response)) {
          // Refresh the list
          const filters: any = {};
          if (filterProductType) filters.productType = filterProductType;
          if (filterItemType) filters.itemType = filterItemType;
          if (filterSubtypeName) filters.subtypeName = filterSubtypeName;
          filters.isActive = true;
          dispatch(fetchItemSubtypeDefinitions(filters));
          setIsFormOpen(false);
        }
      } else if (selectedDefinition?.id) {
        // Edit mode - use single update
        const formData: Partial<ItemSubtypeDefinition> = {
          productType,
          itemType,
          subtypeName,
          optionValue,
          optionLabel: optionLabel || optionValue,
          selectionType,
          uiComponentType,
          isRequired,
          isActive,
          displayOrder,
          optionOrder,
          description: description || null,
        };
        await dispatch(updateItemSubtypeDefinition({ id: selectedDefinition.id, data: formData })).unwrap();
        
        // Refresh the list
        const filters: any = {};
        if (filterProductType) filters.productType = filterProductType;
        if (filterItemType) filters.itemType = filterItemType;
        if (filterSubtypeName) filters.subtypeName = filterSubtypeName;
        filters.isActive = true;
        dispatch(fetchItemSubtypeDefinitions(filters));
        setIsFormOpen(false);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      // Error will be shown via the error state
    }
  };

  const handleDeleteSubtypeGroup = async (group: any) => {
    const groupDefinitions = definitions.filter(
      d => d.productType === group.productType &&
      d.itemType === group.itemType &&
      d.subtypeName === group.subtypeName
    );
    
    if (groupDefinitions.length === 0) {
      alert('No definitions found to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete the entire subtype definition row?\n\nThis will delete the subtype "${group.subtypeName}" with all ${groupDefinitions.length} option(s) for:\n- Product Type: ${group.productType}\n- Item Type: ${group.itemType}\n- Subtype Name: ${group.subtypeName}\n\nThis action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        // Delete all definitions in the group (this deletes the entire row)
        const deletePromises = groupDefinitions.map(def => 
          dispatch(deleteItemSubtypeDefinition(def.id!)).unwrap()
        );
        await Promise.all(deletePromises);
        
        // Refresh the list
        const filters: any = {};
        if (filterProductType) filters.productType = filterProductType;
        if (filterItemType) filters.itemType = filterItemType;
        if (filterSubtypeName) filters.subtypeName = filterSubtypeName;
        filters.isActive = true;
        dispatch(fetchItemSubtypeDefinitions(filters));
      } catch (error: any) {
        console.error('Failed to delete subtype definition row:', error);
        const errorMessage = error?.message || error?.toString() || 'Unknown error';
        alert(`Failed to delete subtype definition row: ${errorMessage}`);
      }
    }
  };

  // Get unique values for filters
  const uniqueProductTypes = Array.from(new Set(definitions.map(d => d.productType)));
  const uniqueItemTypes = Array.from(new Set(definitions.map(d => d.itemType)));
  const uniqueSubtypeNames = Array.from(new Set(definitions.map(d => d.subtypeName)));

  // Group item types by product type from definitions
  const itemTypesByProductType = definitions.reduce((acc, def) => {
    if (!acc[def.productType]) {
      acc[def.productType] = new Set<string>();
    }
    acc[def.productType].add(def.itemType);
    return acc;
  }, {} as Record<string, Set<string>>);

  // Convert Sets to sorted arrays
  const itemTypesGrouped = Object.keys(itemTypesByProductType)
    .sort()
    .map(productType => ({
      productType,
      itemTypes: Array.from(itemTypesByProductType[productType]).sort()
    }));

  // Create a comprehensive grouped list that includes all item types from master
  // For each product type, show item types from definitions if available, otherwise show all master item types
  const allItemTypesGrouped = productTypes.map(productType => {
    const definedItemTypes = itemTypesByProductType[productType];
    // If there are defined item types for this product type, use them
    // Otherwise, show all item types from master
    const itemTypesForProduct = definedItemTypes 
      ? Array.from(definedItemTypes).sort()
      : itemTypes; // Use all master item types if no definitions exist
    
    return {
      productType,
      itemTypes: itemTypesForProduct
    };
  });

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Item Subtype Definitions</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage item subtypes based on product type and item type
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleCreateDefinition}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Definition
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product Type</label>
              <select
                value={filterProductType}
                onChange={(e) => setFilterProductType(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Product Types</option>
                {uniqueProductTypes.map(type => (
                  <option key={type} value={type}>{capitalizeWords(type)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Item Type</label>
              <select
                value={filterItemType}
                onChange={(e) => setFilterItemType(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Item Types</option>
                {itemTypesGrouped.map(group => (
                  <optgroup 
                    key={group.productType} 
                    label={`━━━ ${capitalizeWords(group.productType)} ━━━`}
                    style={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                  >
                    {group.itemTypes.map(itemType => (
                      <option 
                        key={`${group.productType}-${itemType}`} 
                        value={itemType}
                        style={{ paddingLeft: '20px' }}
                      >
                        {itemType}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtype Name</label>
              <select
                value={filterSubtypeName}
                onChange={(e) => setFilterSubtypeName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Subtype Names</option>
                {uniqueSubtypeNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-600">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ minWidth: '1200px' }}>
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Product Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Item Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Subtype Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Selection Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Options</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Required</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.keys(groupedDefinitions).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No subtype definitions found. {canCreate && 'Create your first definition to get started.'}
                      </td>
                    </tr>
                  ) : (
                    Object.values(groupedDefinitions).map((group: any, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{capitalizeWords(group.productType || 'N/A')}</td>
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white whitespace-nowrap">{group.itemType || 'N/A'}</td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{group.subtypeName || 'N/A'}</td>
                        <td className="px-4 py-4 text-sm whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            group.selectionType === 'multi' 
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                            {capitalizeWords(group.selectionType || 'single')}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex flex-wrap gap-1 min-w-[200px]">
                            {group.options && group.options.length > 0 ? (
                              group.options
                                .filter((opt: any) => opt.isActive !== false)
                                .sort((a: any, b: any) => (a.optionOrder || 0) - (b.optionOrder || 0))
                                .map((opt: any) => {
                                  const fullDefinition = definitions.find(d => d.id === opt.id);
                                  return (
                                    <div key={opt.id || Math.random()} className="inline-flex items-center gap-1 group">
                                      <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded">
                                        {opt.label || opt.value || 'N/A'}
                                      </span>
                                    </div>
                                  );
                                })
                            ) : (
                              <span className="text-gray-400 text-xs">No options</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            group.isRequired 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {group.isRequired ? 'Required' : 'Optional'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right text-sm whitespace-nowrap">
                          <div className="flex justify-end gap-2">
                            {canUpdate && (
                              <button
                                onClick={() => {
                                  // Find first option to edit
                                  const firstOption = definitions.find(
                                    d => d.productType === group.productType &&
                                    d.itemType === group.itemType &&
                                    d.subtypeName === group.subtypeName
                                  );
                                  if (firstOption) handleEditDefinition(firstOption);
                                }}
                                className="px-3 py-1 text-sm text-blue-600 hover:text-blue-900 border border-blue-600 hover:border-blue-900 rounded-md"
                                title="Update Subtype"
                              >
                                Update
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => {
                                  handleDeleteSubtypeGroup(group);
                                }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-600"
                                title="Delete Entire Subtype Definition Row"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5 border shadow-lg rounded-md bg-white dark:bg-gray-800 m-4">
              <button
                onClick={() => setIsFormOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <h3 className="text-lg font-medium mb-4">
                {formMode === 'create' ? 'Create Subtype Definitions' : 'Edit Subtype Definition'}
              </h3>

              <div className="space-y-6">
                {/* Product Type and Item Type - Common for all subtypes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Product Type *</label>
                    <select
                      value={productType}
                      onChange={(e) => {
                        setProductType(e.target.value);
                        // Reset subtypes and ref when product type changes to allow fetching new ones
                        if (formMode === 'create') {
                          setSubtypes([]); // Reset to empty - will be populated if existing subtypes found
                          loadedSubtypesRef.current = ''; // Reset to allow fetching for new combination
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={loadingProductTypes}
                    >
                      <option value="">Select Product Type</option>
                      {productTypes.map(type => (
                        <option key={type} value={String(type)}>{capitalizeWords(String(type))}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Item Type *</label>
                    <select
                      value={itemType}
                      onChange={(e) => {
                        setItemType(e.target.value);
                        // Reset subtypes and ref when item type changes to allow fetching new ones
                        if (formMode === 'create') {
                          setSubtypes([]); // Reset to empty - will be populated if existing subtypes found
                          loadedSubtypesRef.current = ''; // Reset to allow fetching for new combination
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={loadingItemTypes}
                    >
                      <option value="">{loadingItemTypes ? 'Loading item types...' : 'Select Item Type'}</option>
                      {productType ? (
                        // If product type is selected, show ALL item types from master (not just configured ones)
                        // This allows creating new product type + item type combinations
                        itemTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))
                      ) : (
                        // If no product type selected, show all item types grouped by product type with clear separation
                        // Use allItemTypesGrouped which includes all master item types for each product type
                        allItemTypesGrouped.map((group) => (
                          <optgroup 
                            key={group.productType} 
                            label={`━━━ ${capitalizeWords(String(group.productType))} ━━━`}
                            style={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                          >
                            {group.itemTypes.map(itemType => (
                              <option 
                                key={`${group.productType}-${itemType}`} 
                                value={itemType}
                                style={{ paddingLeft: '20px' }}
                              >
                                {itemType}
                              </option>
                            ))}
                          </optgroup>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {formMode === 'create' ? (
                  /* Create Mode - Multiple Subtypes */
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <p><strong>Note:</strong> Only subtypes with at least one option value will be created. Subtypes without options will be ignored.</p>
                    </div>
                    {subtypes.map((subtype, subtypeIndex) => (
                      <div key={subtypeIndex} className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-semibold">Subtype {subtypeIndex + 1} (Will be created only if it has options)</h4>
                          <button
                            type="button"
                            onClick={() => removeSubtype(subtypeIndex)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove this subtype"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">Subtype Name</label>
                              <input
                                type="text"
                                value={subtype.subtypeName}
                                onChange={(e) => updateSubtype(subtypeIndex, 'subtypeName', e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., Quality, Finish, Material (optional)"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-1">Selection Type</label>
                              <select
                                value={subtype.selectionType}
                                onChange={(e) => updateSubtype(subtypeIndex, 'selectionType', e.target.value as 'single' | 'multi')}
                                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="single">Single Select</option>
                                <option value="multi">Multi Select</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-1">UI Component Type</label>
                            <select
                              value={subtype.uiComponentType || 'dropdown'}
                              onChange={(e) => updateSubtype(subtypeIndex, 'uiComponentType', e.target.value as 'dropdown' | 'checkbox' | 'radio')}
                              className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="dropdown">Dropdown</option>
                              <option value="checkbox">Checkbox</option>
                              <option value="radio">Radio Button</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Choose how this subtype should be displayed in the order form
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">Display Order</label>
                              <input
                                type="number"
                                value={subtype.displayOrder}
                                onChange={(e) => updateSubtype(subtypeIndex, 'displayOrder', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                min="0"
                              />
                            </div>

                            <div className="flex items-center pt-6">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={subtype.isRequired}
                                  onChange={(e) => updateSubtype(subtypeIndex, 'isRequired', e.target.checked)}
                                  className="mr-2"
                                />
                                <span className="text-sm font-medium">Required</span>
                              </label>
                            </div>
                          </div>

                          {/* Options for this subtype */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="block text-sm font-medium">Options</label>
                              <button
                                type="button"
                                onClick={() => addOption(subtypeIndex)}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <PlusIcon className="h-4 w-4" />
                                Add Option
                              </button>
                            </div>

                            {subtype.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="grid grid-cols-12 gap-2 items-start bg-white dark:bg-gray-800 p-2 rounded">
                                <div className="col-span-4">
                                  <input
                                    type="text"
                                    value={option.value}
                                    onChange={(e) => updateOption(subtypeIndex, optionIndex, 'value', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Value (e.g., Standard)"
                                    required
                                  />
                                </div>
                                <div className="col-span-4">
                                  <input
                                    type="text"
                                    value={option.label}
                                    onChange={(e) => updateOption(subtypeIndex, optionIndex, 'label', e.target.value)}
                                    className="w-full px-2 py-1 text-sm border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Label (optional)"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <input
                                    type="number"
                                    value={option.optionOrder}
                                    onChange={(e) => updateOption(subtypeIndex, optionIndex, 'optionOrder', parseInt(e.target.value) || 0)}
                                    className="w-full px-2 py-1 text-sm border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Order"
                                    min="0"
                                  />
                                </div>
                                <div className="col-span-2 flex justify-end">
                                  {subtype.options.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeOption(subtypeIndex, optionIndex)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      ))}

                    {subtypes.length === 0 ? (
                      <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-md">
                        <p className="text-sm text-gray-500 mb-2">No subtypes added</p>
                        <button
                          type="button"
                          onClick={addSubtype}
                          className="text-blue-600 hover:text-blue-800 flex items-center justify-center gap-2 mx-auto"
                        >
                          <PlusIcon className="h-5 w-5" />
                          Add Subtype (Optional)
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={addSubtype}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-500 hover:text-blue-600 flex items-center justify-center gap-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                        Add Another Subtype
                      </button>
                    )}
                  </div>
                ) : (
                  /* Edit Mode - Single Option (Legacy) */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Subtype Name *</label>
                      <input
                        type="text"
                        value={subtypeName}
                        onChange={(e) => setSubtypeName(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Quality, Finish, Material"
                        required
                        disabled
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Option Value *</label>
                        <input
                          type="text"
                          value={optionValue}
                          onChange={(e) => setOptionValue(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Standard, Premium"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Option Label</label>
                        <input
                          type="text"
                          value={optionLabel}
                          onChange={(e) => setOptionLabel(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Display label (optional)"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Selection Type *</label>
                        <select
                          value={selectionType}
                          onChange={(e) => setSelectionType(e.target.value as 'single' | 'multi')}
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="single">Single Select</option>
                          <option value="multi">Multi Select</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Display Order</label>
                        <input
                          type="number"
                          value={displayOrder}
                          onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">UI Component Type</label>
                      <select
                        value={uiComponentType}
                        onChange={(e) => setUiComponentType(e.target.value as 'dropdown' | 'checkbox' | 'radio')}
                        className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="dropdown">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="radio">Radio Button</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Choose how this subtype should be displayed in the order form
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Option Order</label>
                        <input
                          type="number"
                          value={optionOrder}
                          onChange={(e) => setOptionOrder(parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                        />
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isRequired}
                            onChange={(e) => setIsRequired(e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm font-medium">Required</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md"
                >
                  {formMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default ItemSubtypeDefinitions;

